import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "../lib/api";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  if (!settings) return <div className="text-slate-500 p-8">Loading settings...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        pricing_plans: settings.pricing_plans,
        gov_credentials: settings.gov_credentials,
        social_links: settings.social_links,
        email_enabled: Boolean(settings.email_enabled),
        low_margin_threshold: parseFloat(settings.low_margin_threshold) || 10,
        reminder_config: settings.reminder_config,
      });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const plans = settings.pricing_plans || [];
  const gov = settings.gov_credentials || {};
  const social = settings.social_links || {};
  const rc = settings.reminder_config || { enabled: true, offsets_minutes: [10080, 4320, 1440, 120], overdue_enabled: true };

  const setPlan = (i, k, v) => setSettings((s) => ({ ...s, pricing_plans: s.pricing_plans.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setGov = (k, v) => setSettings((s) => ({ ...s, gov_credentials: { ...s.gov_credentials, [k]: v } }));
  const setSocial = (k, v) => setSettings((s) => ({ ...s, social_links: { ...(s.social_links || {}), [k]: v } }));
  const setRc = (k, v) => setSettings((s) => ({ ...s, reminder_config: { ...(s.reminder_config || { enabled: true, offsets_minutes: [10080, 4320, 1440, 120], overdue_enabled: true }), [k]: v } }));
  const toggleOffset = (m) => {
    const cur = rc.offsets_minutes || [];
    setRc("offsets_minutes", cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => b - a));
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Admin Settings</h1>
        <button data-testid="settings-save-button" onClick={save} disabled={saving} className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70"><Save size={15} /> {saving ? "Saving..." : "Save All"}</button>
      </div>

      <section className="mt-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-pricing-section">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Procurement-as-a-Service Pricing</h2>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          {plans.map((p, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-4">
              <input data-testid={`plan-name-${i}`} className="pg-input !py-2.5" value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} placeholder="Plan name" />
              <input data-testid={`plan-price-${i}`} className="pg-input !py-2.5 mt-2" value={p.price} onChange={(e) => setPlan(i, "price", e.target.value)} placeholder="Price" />
              <textarea data-testid={`plan-features-${i}`} rows={5} className="pg-input !py-2.5 mt-2 text-[13px]" value={(p.features || []).join("\n")}
                onChange={(e) => setPlan(i, "features", e.target.value.split("\n").filter(Boolean))} placeholder="One feature per line" />
            </div>
          ))}
        </div>
      </section>

      <section className="mt-5 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-gov-section">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Government Credentials</h2>
        <p className="text-[13px] text-slate-500 mt-1">Only verified credentials are shown publicly. Empty fields are hidden on the Government page.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div><label className="pg-label">Legal Business Name</label><input data-testid="gov-legal-name-input" className="pg-input !py-2.5" placeholder="As registered in SAM.gov" value={gov.legal_name || ""} onChange={(e) => setGov("legal_name", e.target.value)} /></div>
          <div><label className="pg-label">SAM.gov Registration Status</label><input data-testid="gov-sam-status-input" className="pg-input !py-2.5" placeholder="e.g. Active — expires MM/YYYY" value={gov.sam_status || ""} onChange={(e) => setGov("sam_status", e.target.value)} /></div>
          <div><label className="pg-label">UEI</label><input data-testid="gov-uei-input" className="pg-input !py-2.5" value={gov.uei || ""} onChange={(e) => setGov("uei", e.target.value)} /></div>
          <div><label className="pg-label">CAGE Code</label><input data-testid="gov-cage-input" className="pg-input !py-2.5" value={gov.cage_code || ""} onChange={(e) => setGov("cage_code", e.target.value)} /></div>
          <div><label className="pg-label">NAICS Codes (comma-separated)</label><input data-testid="gov-naics-input" className="pg-input !py-2.5" value={(gov.naics || []).join(", ")} onChange={(e) => setGov("naics", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></div>
          <div><label className="pg-label">NIGP Codes (comma-separated)</label><input data-testid="gov-nigp-input" className="pg-input !py-2.5" value={(gov.nigp || []).join(", ")} onChange={(e) => setGov("nigp", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></div>
          <div><label className="pg-label">Certifications (comma-separated)</label><input data-testid="gov-certs-input" className="pg-input !py-2.5" value={(gov.certifications || []).join(", ")} onChange={(e) => setGov("certifications", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></div>
          <div><label className="pg-label">Contract Vehicles (comma-separated)</label><input data-testid="gov-vehicles-input" className="pg-input !py-2.5" value={(gov.contract_vehicles || []).join(", ")} onChange={(e) => setGov("contract_vehicles", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Past Performance (one per line)</label><textarea data-testid="gov-past-perf-input" rows={3} className="pg-input !py-2.5" value={(gov.past_performance || []).join("\n")} onChange={(e) => setGov("past_performance", e.target.value.split("\n").filter(Boolean))} /></div>
        </div>
      </section>

      <section className="mt-5 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-social-section">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Social Media Links</h2>
        <p className="text-[13px] text-slate-500 mt-1">Paste full profile URLs (starting with https://). Empty fields hide that icon from the website footer.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div><label className="pg-label">Facebook</label><input data-testid="social-facebook-input" className="pg-input !py-2.5" placeholder="https://www.facebook.com/yourpage" value={social.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} /></div>
          <div><label className="pg-label">Instagram</label><input data-testid="social-instagram-input" className="pg-input !py-2.5" placeholder="https://www.instagram.com/yourprofile" value={social.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} /></div>
          <div><label className="pg-label">TikTok</label><input data-testid="social-tiktok-input" className="pg-input !py-2.5" placeholder="https://www.tiktok.com/@yourprofile" value={social.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} /></div>
          <div><label className="pg-label">LinkedIn</label><input data-testid="social-linkedin-input" className="pg-input !py-2.5" placeholder="https://www.linkedin.com/company/yourcompany" value={social.linkedin || ""} onChange={(e) => setSocial("linkedin", e.target.value)} /></div>
        </div>
      </section>

      <EmailSection settings={settings} setSettings={setSettings} rc={rc} setRc={setRc} toggleOffset={toggleOffset} />

      <DigestSection settings={settings} setSettings={setSettings} />

      <RemittanceSection settings={settings} setSettings={setSettings} />

      <MaintenanceSection />
    </div>
  );
}

const OFFSETS = [
  { minutes: 10080, label: "7 days before" },
  { minutes: 4320, label: "3 days before" },
  { minutes: 1440, label: "24 hours before" },
  { minutes: 120, label: "2 hours before" },
];

function EmailSection({ settings, setSettings, rc, setRc, toggleOffset }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/admin/email-status").then((r) => setStatus(r.data)).catch(() => setStatus({}));
  }, []);
  return (
    <section className="mt-5 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-email-section">
      <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Email &amp; Deadline Reminders</h2>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span data-testid="email-provider-status" className={`px-3 py-1.5 rounded-full text-[12px] font-[Poppins] font-semibold ${status?.provider_configured ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
          {status === null ? "Checking..." : status.provider_configured ? "Email provider configured" : "Email provider not configured — emails are logged, not sent"}
        </span>
      </div>

      <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
        <input data-testid="email-enabled-toggle" type="checkbox" className="mt-1" checked={Boolean(settings.email_enabled)}
          onChange={(e) => setSettings((s) => ({ ...s, email_enabled: e.target.checked }))} />
        <span className="text-[13.5px] text-slate-600"><strong className="text-[var(--pg-navy)]">Send real emails.</strong> Keep this off until the RFQ workflow has been tested. While off, every email is prepared and logged so you can review exactly what would be sent.</span>
      </label>

      <div className="mt-5 border-t border-slate-100 pt-5">
        <h3 className="font-[Poppins] font-semibold text-[14px] text-[var(--pg-navy)]">Low-Margin Warning Threshold</h3>
        <p className="text-[13px] text-slate-500 mt-1">Quotes with a gross margin below this percentage show a "Margin Review Required" warning (does not block quote creation).</p>
        <div className="mt-2 flex items-center gap-2">
          <input data-testid="low-margin-threshold-input" type="number" step="0.5" min="0" max="95" className="pg-input !py-2 !w-[110px]"
            value={settings.low_margin_threshold ?? 10}
            onChange={(e) => setSettings((s) => ({ ...s, low_margin_threshold: e.target.value }))} />
          <span className="text-[13px] text-slate-500">%</span>
        </div>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-5">
        <h3 className="font-[Poppins] font-semibold text-[14px] text-[var(--pg-navy)]">Deadline Reminders</h3>
        <p className="text-[13px] text-slate-500 mt-1">Automated deadline reminders help your procurement team stay ahead of important bid and RFQ deadlines. Covers government bids, RFQ required dates, supplier quote deadlines, customer quote expirations and delivery dates.</p>
        <label className="mt-3 flex items-center gap-2.5 cursor-pointer">
          <input data-testid="reminders-enabled-toggle" type="checkbox" checked={Boolean(rc.enabled)} onChange={(e) => setRc("enabled", e.target.checked)} />
          <span className="text-[13.5px] text-slate-600">Enable deadline reminders</span>
        </label>
        <div className="mt-3 flex flex-wrap gap-2">
          {OFFSETS.map((o) => (
            <button type="button" key={o.minutes} data-testid={`reminder-offset-${o.minutes}`} onClick={() => toggleOffset(o.minutes)}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-[Poppins] font-semibold border transition-colors ${(rc.offsets_minutes || []).includes(o.minutes) ? "bg-[var(--pg-navy)] text-white border-[var(--pg-navy)]" : "bg-white text-slate-500 border-slate-200 hover:border-[var(--pg-accent)]"}`}>
              {o.label}
            </button>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2.5 cursor-pointer">
          <input data-testid="overdue-enabled-toggle" type="checkbox" checked={Boolean(rc.overdue_enabled)} onChange={(e) => setRc("overdue_enabled", e.target.checked)} />
          <span className="text-[13.5px] text-slate-600">Also send a notification when a deadline has passed</span>
        </label>
      </div>
    </section>
  );
}

function MaintenanceSection() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data?dry_run=true");
      setScan(data);
      if (data.total === 0) toast.success("No test data found — database is clean");
    } catch { toast.error("Scan failed"); }
    finally { setBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Permanently delete ${scan.total} test records? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data?dry_run=false");
      toast.success(`Removed ${data.total} test records`);
      setScan(null);
    } catch { toast.error("Cleanup failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-5 bg-white rounded-xl border border-red-100 p-6" data-testid="settings-maintenance-section">
      <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Data Maintenance</h2>
      <p className="text-[13px] text-slate-500 mt-1">
        Sweep test records left over from development and QA runs — RFQs, quotes, orders, invoices, tasks and accounts whose
        name starts with <code className="bg-slate-100 px-1 rounded">TEST</code> or whose email starts with <code className="bg-slate-100 px-1 rounded">test_</code>.
        Real customer data is never touched. A scan shows exactly what would be removed before anything is deleted.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="cleanup-scan-button" onClick={runScan} disabled={busy}
          className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">{busy ? "Working..." : "Scan for Test Data"}</button>
        {scan && scan.total > 0 && (
          <button data-testid="cleanup-delete-button" onClick={runCleanup} disabled={busy}
            className="text-[13px] font-[Poppins] font-semibold px-5 py-2.5 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-70">
            Delete {scan.total} Test Records
          </button>
        )}
      </div>
      {scan && scan.total > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="cleanup-scan-results">
          {Object.entries(scan.counts).map(([k, v]) => (
            <span key={k} className="px-3 py-1.5 rounded-full text-[12px] font-[Poppins] font-semibold bg-slate-100 text-slate-600">
              {k.replace(/_/g, " ")}: {v}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

const DIGEST_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function DigestSection({ settings, setSettings }) {
  const [recipients, setRecipients] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/digest/recipients").then((r) => setRecipients(r.data)).catch(() => {});
  }, []);

  const saveSchedule = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.put("/admin/settings", patch);
      toast.success("Digest schedule updated");
    } catch { toast.error("Failed to update digest schedule"); }
  };

  const toggleRecipient = async (email, optOut) => {
    setRecipients((rs) => rs.map((r) => (r.email === email ? { ...r, digest_opt_out: optOut } : r)));
    try {
      await api.post("/admin/digest/recipients", { email, opt_out: optOut });
    } catch {
      toast.error("Failed to update recipient");
      setRecipients((rs) => rs.map((r) => (r.email === email ? { ...r, digest_opt_out: !optOut } : r)));
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/digest/run");
      toast.success(`Digest sent to ${data.recipients.length} recipient${data.recipients.length === 1 ? "" : "s"}`);
    } catch { toast.error("Digest send failed"); }
    finally { setBusy(false); }
  };

  const day = settings.digest_day ?? 0;
  const hour = settings.digest_hour_utc ?? 13;
  const etHour = ((hour - 5) + 24) % 24;

  return (
    <section className="mt-5 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-digest-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Weekly Digest</h2>
          <p className="text-[13px] text-slate-500 mt-1">Email summary of open RFQs, expiring quotes and unpaid invoices, sent to your team once a week.</p>
        </div>
        <button data-testid="digest-send-now" onClick={sendNow} disabled={busy}
          className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">{busy ? "Sending..." : "Send Digest Now"}</button>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <label className="pg-label">Day of week</label>
          <select data-testid="digest-day-select" className="pg-input !py-2.5" value={day}
            onChange={(e) => saveSchedule({ digest_day: parseInt(e.target.value, 10) })}>
            {DIGEST_DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="pg-label">Send time (UTC)</label>
          <select data-testid="digest-hour-select" className="pg-input !py-2.5" value={hour}
            onChange={(e) => saveSchedule({ digest_hour_utc: parseInt(e.target.value, 10) })}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>)}
          </select>
          <p className="mt-1 text-[11.5px] text-slate-400">≈ {etHour % 12 === 0 ? 12 : etHour % 12}:00 {etHour < 12 ? "AM" : "PM"} Houston time (CDT/EST varies)</p>
        </div>
      </div>
      <div className="mt-6">
        <div className="text-[12px] font-[Poppins] font-bold uppercase tracking-wider text-slate-500">Recipients</div>
        <div className="mt-3 space-y-2">
          {recipients.map((r) => (
            <label key={r.email} data-testid={`digest-recipient-${r.email}`}
              className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg px-4 py-2.5">
              <span className="min-w-0">
                <span className="block text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)] truncate">{r.name || r.email}</span>
                <span className="block text-[12px] text-slate-400 truncate">{r.email} · {(r.role || "").replace(/_/g, " ")}</span>
              </span>
              <input type="checkbox" data-testid={`digest-toggle-${r.email}`}
                className="w-4.5 h-4.5 accent-[var(--pg-accent)]"
                checked={!r.digest_opt_out}
                onChange={(e) => toggleRecipient(r.email, !e.target.checked)} />
            </label>
          ))}
          {recipients.length === 0 && <p className="text-[13px] text-slate-400">No staff accounts found.</p>}
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400">Checked staff receive the digest. Changes apply from the next scheduled send.</p>
      </div>
    </section>
  );
}

const REMIT_FIELDS = [
  ["bank_name", "Bank Name", "e.g. JPMorgan Chase"],
  ["account_name", "Account Name", "e.g. Patrick Global Procurement Services LLC"],
  ["account_number", "Account Number", ""],
  ["routing_number", "Routing (ABA)", ""],
  ["swift", "SWIFT / BIC", "For international wires (optional)"],
];

function RemittanceSection({ settings, setSettings }) {
  const [saving, setSaving] = useState(false);
  const r = settings.remittance || {};
  const set = (k) => (e) => setSettings((s) => ({ ...s, remittance: { ...(s.remittance || {}), [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { remittance: r });
      toast.success("Remittance details saved — they now appear on quote & invoice PDFs");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <section className="mt-5 bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-remittance-section">
      <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Bank / Remittance Details</h2>
      <p className="text-[13px] text-slate-500 mt-1">
        Shown in the "Remit Payment To" block on quote and invoice PDFs. Leave blank to omit the block entirely.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-4 max-w-2xl">
        {REMIT_FIELDS.map(([k, label, ph]) => (
          <div key={k}>
            <label className="pg-label">{label}</label>
            <input data-testid={`remit-${k}-input`} className="pg-input !py-2.5" placeholder={ph} value={r[k] || ""} onChange={set(k)} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="pg-label">Payment Instructions</label>
          <input data-testid="remit-instructions-input" className="pg-input !py-2.5"
            placeholder="e.g. Please include the invoice number in your wire memo" value={r.instructions || ""} onChange={set("instructions")} />
        </div>
      </div>
      <button data-testid="remit-save-button" onClick={save} disabled={saving}
        className="mt-5 btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">{saving ? "Saving..." : "Save Remittance Details"}</button>
    </section>
  );
}
